import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { notificationService } from '@/services/notificationService';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/providers/ToastProvider';
import { mockNotifications } from '@/mock/misc';
import type { Notification } from '@/types';

interface NotificationsContextValue {
  items: Notification[];
  unreadCount: number;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  /**
   * Phase-1 helper to simulate an inbound notification (used by the demo
   * button in the portal header). Phase 2 swaps the polling effect below
   * for a Supabase Realtime channel and this method goes away.
   */
  simulateInbound: (n: Pick<Notification, 'category' | 'title' | 'body'> & { link?: string | null }) => void;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

const useMockAuth = (import.meta.env.VITE_USE_MOCK_AUTH ?? 'true') === 'true';

/**
 * Subscribes to the user's notifications and exposes them to the rest of
 * the app via context. Toasts new notifications as they arrive.
 *
 *   • Phase 1 (mock auth): polls notificationService every 8 seconds and
 *     diffs against a known-IDs set so a new notification triggers a toast.
 *   • Phase 2: replace the polling effect with a Supabase Realtime
 *     subscription:
 *
 *       supabase.channel(`user-notifications:${user.id}`)
 *         .on('postgres_changes', { event: 'INSERT', table: 'notifications', filter: `user_id=eq.${user.id}` },
 *             (payload) => onIncoming(payload.new))
 *         .subscribe();
 *
 * Either way, components consume `useNotifications()` and don't change.
 */
export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const toast = useToast();
  const [items, setItems] = useState<Notification[]>([]);
  const knownIds = useRef<Set<string>>(new Set());
  const initialised = useRef(false);

  const refresh = useCallback(async () => {
    if (!user) return;
    const next = await notificationService.listForUser(user.id);

    // First load = silently seed the known set; never toast on initial load.
    if (!initialised.current) {
      knownIds.current = new Set(next.map((n) => n.id));
      initialised.current = true;
      setItems(next);
      return;
    }

    // Toast anything new since the last poll
    for (const n of next) {
      if (!knownIds.current.has(n.id) && !n.read) {
        toast.info(n.title, n.body);
        knownIds.current.add(n.id);
      }
    }
    setItems(next);
  }, [user, toast]);

  // Initial load + polling (Phase 1).
  //
  // Pause polling while the tab is hidden so we don't drain battery and
  // burn Supabase quota for users who left the app open in a background
  // tab. Resume immediately on focus and force-refresh so they see the
  // latest state.
  useEffect(() => {
    if (!user) {
      setItems([]);
      knownIds.current = new Set();
      initialised.current = false;
      return;
    }
    refresh();
    if (!useMockAuth) {
      // Phase 2 will subscribe to Supabase Realtime here instead of polling.
      return;
    }

    let interval: number | null = null;
    const start = () => {
      if (interval != null) return;
      interval = window.setInterval(refresh, 8_000);
    };
    const stop = () => {
      if (interval == null) return;
      window.clearInterval(interval);
      interval = null;
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        refresh();   // catch up on what we missed while hidden
        start();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    if (!document.hidden) start();

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user, refresh]);

  const markRead = useCallback(async (id: string) => {
    await notificationService.markRead(id);
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }, []);

  const markAllRead = useCallback(async () => {
    if (!user) return;
    await notificationService.markAllRead(user.id);
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
  }, [user]);

  const simulateInbound = useCallback(
    (n: Pick<Notification, 'category' | 'title' | 'body'> & { link?: string | null }) => {
      if (!user) return;
      const next: Notification = {
        id: `n_${crypto.randomUUID().slice(0, 8)}`,
        userId: user.id,
        category: n.category,
        title: n.title,
        body: n.body,
        link: n.link ?? null,
        read: false,
        createdAt: new Date().toISOString(),
      };
      mockNotifications.unshift(next);
      // Pick it up on the next poll tick — keeps the code path identical to
      // production. (Toast fires from refresh().)
      refresh();
    },
    [user, refresh],
  );

  const unreadCount = items.filter((n) => !n.read).length;

  return (
    <NotificationsContext.Provider value={{ items, unreadCount, markRead, markAllRead, simulateInbound }}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotifications must be used inside NotificationsProvider');
  return ctx;
}
